import { defineRule, configure } from 'vee-validate';
import {
  required,
  min,
  max,
  numeric,
  min_value,
  max_value,
  integer,
  length,
  is_not
} from '@vee-validate/rules';
import { localize } from '@vee-validate/i18n';
import i18n from './i18n';
import { m } from './moment';
import validationMessages from '@/plugins/i18n/no/validations';

configure({
  // this will be used to generate messages.
  generateMessage: localize("no", {
    messages: validationMessages
  })
});

defineRule("required", required);

defineRule("min", min);

defineRule("max", max);

defineRule("numeric", numeric);

defineRule("min_value", min_value);

defineRule("max_value", max_value);

defineRule("required_if", (value, [isRequired]) =>
  isRequired ?
    required(value)
    :
    false);

defineRule("integer", integer);

defineRule("length", length);

defineRule("is_not", is_not);

defineRule("date", value => {
  const date = m(value, i18n.global.t("commonStrings.dateDisplayFormat"));
  return date.isValid();
});

defineRule("time", value => {
  const [hour, minute] = value.split(":");

  const date = m({
    year: 1999,
    month: 1,
    date: 1,
    hour: parseInt(hour, 10),
    minute: parseInt(minute, 10)
  });

  return date.isValid();
});

defineRule("finance_account", value => {
  if (!value?.length) {
    return true;
  }
  const re = new RegExp(/^\d{4,5}$/);

  return re.test(value);
});

const getDecimalRegexPart = function (decimals) {
  if (decimals === '*') {
    return '+';
  }

  return `{1,${decimals}}`;
};

defineRule("decimal",
// Set return type to avoid sonarcloud issue
/** @returns {any} */
(value, [decimals = '*', separator = '.'] = []) => {
  if (!value?.length) {
    return true;
  }

  if (Number(decimals) === 0) {
    return /^-?\d*$/.test(value) ;
  }

  const regex = new RegExp(`^[-+]?\\d*(\\${separator}\\d${getDecimalRegexPart(decimals)})?([eE]{1}[-]?\\d+)?$`);

  return regex.test(value) ||
    i18n.global.t(`validations.decimal`, { decimals });
});

defineRule("required_if_any", (value, [enabled]) => {
  const valid = !enabled || (value && value.length > 0);

  return valid;
});

defineRule("name_list", (value, [ min_word_count = "1", min_word_length = "1" ] = []) => {
  if (!value?.length) {
    return true;
  }

  return value
    .map(x => x.split(" "))
    .every(x => x.length >= min_word_count &&
      x.every(y => y.length >= min_word_length));
});

defineRule("max_list_items", (value, [max_count]) => {
  return !value?.length || !max_count || value.length <= max_count;
});


defineRule("max_list_item_length", (value, [max_length] = {}) => {
  return value.every(x => x.length <= max_length);
});

const getComputedChecksum = function (remainder) {
  if (remainder > 0) {
    return 11 - remainder;
  }
  return 0;
};

defineRule("account_number", value => {
  if (!value?.length) {
    return true;
  }

  if (value.length !== 11) {
    return false;
  }
  if (!(/^\d{11}$/.test(value))) {
    return false;
  }

  const checksum = Number.parseInt(value.charAt(10), 10);
  const account = value.substring(0, 10);

  let sum = 0;
  for (let index = 0; index < 10; index++) {
    sum += Number.parseInt(account.charAt(index), 10) * (7 - (index + 2) % 6);
  }

  const remainder = sum % 11;

  const computedChecksum = getComputedChecksum(remainder);

  return computedChecksum === checksum;
});
